/**
 * Shop Home Screen
 * Main dashboard after successful login
 * Displays shop details, QR code with download and share options
 * Uses dynamic theme colors from shop configuration
 */

import React, { useEffect, useState, useRef } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Modal,
  Alert,
} from 'react-native';
import { useAuthTokens, shopManagementService, menuService, useThemeColors, useThemeAccent, getCustomerShopMenuUrl, storageService, devZoneService, type ApiKeyResponse, type CreateApiKeyRequest } from '@app/core';
import { Card, Text, Button } from '@app/design-system';
import { colors } from '@app/design-system';
import { ShopHeader } from '../components/ShopHeader';

interface ShopHomeScreenProps {
  onManageShop?: () => void;
  onManageUser?: () => void;
  onManageCatalog?: () => void;
  onManageOrders?: () => void;
  onWebsiteBuilder?: () => void;
  onLogout?: () => void;
}

export const ShopHomeScreen: React.FC<ShopHomeScreenProps> = ({
  onManageShop,
  onManageUser,
  onManageCatalog,
  onManageOrders,
  onWebsiteBuilder,
  onLogout,
}) => {
  const [shopDetails, setShopDetails] = useState<any>(null);
  const [qrCode, setQrCode] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [enhancedQRPreview, setEnhancedQRPreview] = useState<string | null>(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [showCardBackgroundPicker, setShowCardBackgroundPicker] = useState(false);
  const [cardBackgroundColor, setCardBackgroundColor] = useState('#FFFFFF');
  const [shopImageUrl, setShopImageUrl] = useState<string | null>(null);
  const [loadingShopImage, setLoadingShopImage] = useState(false);

  // DevZone B2B API Management State
  const [apiKeys, setApiKeys] = useState<ApiKeyResponse[]>([]);
  const [loadingApiKeys, setLoadingApiKeys] = useState(false);
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [showKeyDetailsModal, setShowKeyDetailsModal] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKeyResponse | null>(null);
  const [newKeyData, setNewKeyData] = useState<CreateApiKeyRequest>({
    name: '',
    description: '',
    scopes: [],
    allowedOrigins: [],
    expiresInDays: 365,
  });
  const [availableScopes, setAvailableScopes] = useState<{ [key: string]: string }>({});
  const [createdKeySecret, setCreatedKeySecret] = useState<string | null>(null);
  const [rotatedKeySecret, setRotatedKeySecret] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [creatingApiKey, setCreatingApiKey] = useState(false);

  const tokens = useAuthTokens();
  const loadedRef = useRef(false); // Prevent duplicate calls
  const themeColors = useThemeColors();
  const accentColor = useThemeAccent();

  // Predefined background colors for business cards
  const cardBackgroundColors = [
    { name: 'White', value: '#FFFFFF' },
    { name: 'Light Gray', value: '#F5F5F5' },
    { name: 'Cream', value: '#FFFEF0' },
    { name: 'Light Blue', value: '#E3F2FD' },
    { name: 'Light Green', value: '#E8F5E9' },
    { name: 'Light Pink', value: '#FCE4EC' },
    { name: 'Light Yellow', value: '#FFFDE7' },
    { name: 'Light Orange', value: '#FFF3E0' },
  ];

  /**
   * Calculate appropriate text color based on background brightness
   * Returns dark text for light backgrounds, light text for dark backgrounds
   */
  const getTextColor = (backgroundColor: string): string => {
    // Remove # if present
    const hex = backgroundColor.replace('#', '');

    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calculate relative luminance (perceived brightness)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return dark text for light backgrounds, light text for dark backgrounds
    return luminance > 0.5 ? '#1a1a1a' : '#FFFFFF';
  };

  // Get dynamic text colors based on selected background
  const primaryTextColor = getTextColor(cardBackgroundColor);
  const secondaryTextColor = getTextColor(cardBackgroundColor) === '#1a1a1a' ? '#333333' : '#E0E0E0';
  const tertiaryTextColor = getTextColor(cardBackgroundColor) === '#1a1a1a' ? '#555555' : '#CCCCCC';

  // Configurable QR Code text
  const QR_CODE_TEXT =
    'Scan me to know latest items and price in my shop.';

  useEffect(() => {
    // Only load once
    if (!loadedRef.current && tokens?.shopId) {
      loadedRef.current = true;
      loadShopDetails();
      // Load DevZone API keys and scopes
      initializeDevZone();
    }
  }, [tokens?.shopId]);

  // Generate enhanced QR preview when data is ready
  useEffect(() => {
    if (shopDetails && qrCode?.qrCode && !generatingPreview) {
      // Regenerate preview when shop image URL changes or initially
      generateEnhancedQRPreview();
    }
  }, [shopDetails, qrCode, shopImageUrl]);

  // Load shop image when shopDetails is loaded
  useEffect(() => {
    if (shopDetails?.shopImage) {
      loadShopImage(shopDetails.shopImage);
    }
  }, [shopDetails?.shopImage]);

  /**
   * Load shop image from blob path
   * Converts blob path to temporary read URL
   */
  const loadShopImage = async (blobPath: string) => {
    try {
      setLoadingShopImage(true);
      console.log('📸 [ShopImage] Loading image from blob path:', blobPath);

      // Check if it's already a full URL (backward compatibility)
      if (blobPath.startsWith('http://') || blobPath.startsWith('https://')) {
        console.log('📸 [ShopImage] Using direct URL:', blobPath);
        setShopImageUrl(blobPath);
        setImageError(false);
        return;
      }

      // Get read URL from blob path
      const readUrl = await storageService.getReadUrl(blobPath);
      console.log('✅ [ShopImage] Read URL obtained successfully');
      setShopImageUrl(readUrl);
      setImageError(false);
    } catch (error) {
      console.error('❌ [ShopImage] Failed to load image:', error);
      setImageError(true);
      setShopImageUrl(null);
    } finally {
      setLoadingShopImage(false);
    }
  };

  /**
   * Initialize DevZone B2B API Management
   * Sets static scopes and loads existing API keys
   */
  const initializeDevZone = async () => {
    try {
      console.log('🔧 [DevZone] Initializing DevZone API Management...');

      // Set static available scopes
      const staticScopes = {
        'catalogs:read': 'Read product catalog information',
        'catalogs:write': 'Create and update products',
        'orders:read': 'Read order information',
        'orders:write': 'Create and update orders',
        'shops:read': 'Read shop information',
        'storage:read': 'Read storage files',
        'storage:write': 'Upload and manage storage files',
      };

      setAvailableScopes(staticScopes);
      console.log('✅ [DevZone] Static scopes initialized');

      // Load existing API keys
      await loadApiKeys();
    } catch (error) {
      console.error('❌ [DevZone] Failed to initialize DevZone:', error);
    }
  };

  /**
   * Load shop details from API
   * Optimized to prevent duplicate calls using ref tracking
   */
  const loadShopDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!tokens?.shopId) {
        throw new Error('Shop ID not found in token');
      }

      // Get shop details which includes QR code
      const shopData = await shopManagementService.getShop(tokens.shopId);
      setShopDetails(shopData);

      // Use QR code from shop object instead of separate API call
      if (shopData?.qrCode) {
        setQrCode({
          qrCode: shopData.qrCode,
          qrCodeUrl: shopData.qrCodeUrl,
        });
      } else {
        // If shop doesn't have QR code, try to get or generate one
        try {
          const qrData = await menuService.getQRCode(tokens.shopId);
          setQrCode(qrData);
        } catch (qrError) {
          console.warn('QR code not found, attempting to generate new one');
          try {
            const newQr = await menuService.generateQRCode(tokens.shopId);
            setQrCode(newQr);
          } catch (generateError) {
            console.error('Failed to generate QR code:', generateError);
          }
        }
      }

      if (!shopData) {
        throw new Error('Failed to load shop details');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load shop details';
      setError(errorMessage);
      console.error('Error loading shop details:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate enhanced QR code preview for display
   * Creates the same design that will be downloaded
   */
  const generateEnhancedQRPreview = async () => {
    try {
      setGeneratingPreview(true);
      console.log('🎨 [QR Preview] Generating enhanced QR preview...');

      if (!qrCode?.qrCode) {
        console.warn('⚠️ [QR Preview] QR code not available yet');
        return;
      }

      if (typeof window === 'undefined') {
        return;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.error('❌ Could not create canvas context');
        return;
      }

      // Set canvas dimensions - Rectangle format like PhonePe/Paytm QR codes
      const canvasWidth = 1000;
      const canvasHeight = 1400;
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // Load images
      const qrImg = document.createElement('img') as HTMLImageElement;
      const bgImg = shopDetails?.shopImage ? document.createElement('img') as HTMLImageElement : null;

      const imagePromise = new Promise<void>((resolve, reject) => {
        let qrLoaded = false;
        let bgLoaded = !bgImg;
        let qrImageValid = false;
        let bgImageValid = false;

        // Timeout to prevent infinite loading
        const timeout = setTimeout(() => {
          console.warn('⏱️ [QR Preview] Image loading timeout, proceeding anyway...');
          qrLoaded = true;
          bgLoaded = true;
          checkAllLoaded();
        }, 5000); // 5 second timeout

        const checkAllLoaded = () => {
          if (qrLoaded && bgLoaded) {
            clearTimeout(timeout);
            try {
              console.log('🎨 [QR Preview] All images loaded, rendering preview...');

              // Draw background
              if (bgImg && shopImageUrl && bgImageValid && bgImg.complete && bgImg.naturalWidth > 0) {
                const bgAspect = bgImg.width / bgImg.height;
                const canvasAspect = canvasWidth / canvasHeight;

                let drawWidth, drawHeight, offsetX, offsetY;

                if (bgAspect > canvasAspect) {
                  drawHeight = canvasHeight;
                  drawWidth = bgImg.width * (canvasHeight / bgImg.height);
                  offsetX = (canvasWidth - drawWidth) / 2;
                  offsetY = 0;
                } else {
                  drawWidth = canvasWidth;
                  drawHeight = bgImg.height * (canvasWidth / bgImg.width);
                  offsetX = 0;
                  offsetY = (canvasHeight - drawHeight) / 2;
                }

                ctx.drawImage(bgImg, offsetX, offsetY, drawWidth, drawHeight);
                ctx.fillStyle = 'rgba(255, 255, 255, 0.60)'; // 60% transparent overlay
                ctx.fillRect(0, 0, canvasWidth, canvasHeight);
              } else {
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, canvasWidth, canvasHeight);
              }

              // Draw shop name - larger font and positioned lower with smart wrapping
              const shopName = shopDetails?.name || 'My Shop';
              ctx.fillStyle = '#1a1a1a';
              ctx.font = 'bold 72px Arial, sans-serif';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'top';

              const maxNameWidth = canvasWidth - 100; // 900px max width
              const maxNameLines = 3; // Limit to 3 lines to prevent overlap
              const nameWords = shopName.split(' ');
              let nameLines: string[] = [];
              let nameLine = '';

              // Word-based wrapping
              nameWords.forEach((word) => {
                const testLine = nameLine + word + ' ';
                const metrics = ctx.measureText(testLine);

                // Check if single word is too long - needs character wrapping
                if (ctx.measureText(word).width > maxNameWidth) {
                  // Finish current line if it exists
                  if (nameLine.trim()) {
                    nameLines.push(nameLine.trim());
                    nameLine = '';
                  }

                  // Character-level wrapping for very long word
                  let charLine = '';
                  for (let char of word) {
                    const testChar = charLine + char;
                    if (ctx.measureText(testChar).width > maxNameWidth) {
                      nameLines.push(charLine);
                      charLine = char;
                    } else {
                      charLine = testChar;
                    }
                  }
                  if (charLine) {
                    nameLine = charLine + ' ';
                  }
                } else if (metrics.width > maxNameWidth && nameLine) {
                  // Normal word wrapping
                  nameLines.push(nameLine.trim());
                  nameLine = word + ' ';
                } else {
                  nameLine = testLine;
                }
              });

              // Add final line
              if (nameLine.trim()) {
                nameLines.push(nameLine.trim());
              }

              // Limit to max lines and add ellipsis if needed
              if (nameLines.length > maxNameLines) {
                nameLines = nameLines.slice(0, maxNameLines);
                const lastLine = nameLines[maxNameLines - 1];
                nameLines[maxNameLines - 1] = lastLine.slice(0, lastLine.length - 3) + '...';
              }

              // Draw all lines
              let nameY = 90;
              const nameLineHeight = 85;
              nameLines.forEach((line, index) => {
                ctx.fillText(line, canvasWidth / 2, nameY);
                nameY += nameLineHeight;
              });

              let currentY = nameY + 30;

              // Draw instructional text
              ctx.fillStyle = '#333333';
              ctx.font = 'bold 36px Arial, sans-serif';
              ctx.fillText('SCAN HERE to See Our Best Products', canvasWidth / 2, currentY);
              currentY += 60;

              // Draw QR code - centered in rectangular format
              const qrSize = 650;
              const qrX = (canvasWidth - qrSize) / 2;
              const qrY = currentY + 40;
              const qrPadding = 25;

              ctx.fillStyle = '#FFFFFF';
              ctx.fillRect(qrX - qrPadding, qrY - qrPadding, qrSize + (qrPadding * 2), qrSize + (qrPadding * 2));
              ctx.strokeStyle = '#000000';
              ctx.lineWidth = 4;
              ctx.strokeRect(qrX - qrPadding, qrY - qrPadding, qrSize + (qrPadding * 2), qrSize + (qrPadding * 2));

              // Only draw QR if image is valid
              if (qrImageValid && qrImg.complete && qrImg.naturalWidth > 0) {
                ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
              } else {
                // Draw placeholder if QR image is broken
                ctx.fillStyle = '#f0f0f0';
                ctx.fillRect(qrX, qrY, qrSize, qrSize);
                ctx.fillStyle = '#666666';
                ctx.font = '24px Arial, sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText('QR Code', qrX + qrSize / 2, qrY + qrSize / 2);
              }

              // Draw footer - positioned higher with trademark
              const footerY = canvasHeight - 120;
              ctx.fillStyle = '#666666';
              ctx.font = '26px Arial, sans-serif';
              ctx.fillText('Powered by Bynaryx Labs™', canvasWidth / 2, footerY);

              // Convert to base64 for preview
              const previewDataUrl = canvas.toDataURL('image/png', 1.0);
              setEnhancedQRPreview(previewDataUrl);
              console.log('✅ [QR Preview] Enhanced QR preview generated successfully');
              resolve();
            } catch (drawError) {
              console.error('❌ [QR Preview] Error drawing preview:', drawError);
              reject(drawError);
            }
          }
        };

        qrImg.onload = () => {
          console.log('✅ [QR Preview] QR code image loaded');
          if (qrImg.complete && qrImg.naturalWidth > 0) {
            qrImageValid = true;
            qrLoaded = true;
            checkAllLoaded();
          } else {
            console.warn('⚠️ [QR Preview] QR image loaded but invalid');
            qrLoaded = true;
            checkAllLoaded();
          }
        };

        qrImg.onerror = (e) => {
          console.error('❌ [QR Preview] Failed to load QR code image:', e);
          qrImageValid = false;
          qrLoaded = true; // Continue anyway to show preview without QR
          checkAllLoaded();
        };

        if (bgImg && shopImageUrl) {
          console.log('📥 [QR Preview] Loading shop background image');
          bgImg.onload = () => {
            console.log('✅ [QR Preview] Shop background image loaded');
            if (bgImg.complete && bgImg.naturalWidth > 0) {
              bgImageValid = true;
            }
            bgLoaded = true;
            checkAllLoaded();
          };

          bgImg.onerror = () => {
            console.warn('⚠️ [QR Preview] Failed to load shop background image, continuing without it');
            bgImageValid = false;
            bgLoaded = true;
            checkAllLoaded();
          };

          bgImg.crossOrigin = 'anonymous';
          bgImg.src = shopImageUrl;
        }

        // Set QR code image with crossOrigin
        qrImg.crossOrigin = 'anonymous';
        qrImg.src = qrCode.qrCode;

        // Trigger onload if already loaded (cached)
        if (qrImg.complete && qrImg.naturalWidth > 0) {
          console.log('✅ [QR Preview] QR image already cached');
          qrImageValid = true;
          qrLoaded = true;
          checkAllLoaded();
        }
      });

      await imagePromise;
    } catch (error) {
      console.error('❌ [QR Preview] Failed to generate preview:', error);
    } finally {
      setGeneratingPreview(false);
    }
  };

  /**
   * Download Business Cards as A4 Printable PDF/HTML
   * Creates a downloadable HTML file with 12 business cards (90mm x 55mm each) optimized for A4 printing
   */
  const downloadBusinessCardPDFFile = async () => {
    try {
      console.log('📥 [Business Card Download] Starting PDF file generation...');

      if (!qrCode?.qrCode) {
        console.error('❌ QR code not available');
        Alert.alert('Error', 'QR code not available. Please try again.');
        return;
      }

      if (typeof window === 'undefined') {
        return;
      }

      // Create HTML content for printable business cards
      const shopName = shopDetails?.name || 'My Shop';
      const mobileNumber = `${shopDetails?.mobileCountryCode}${shopDetails?.mobileNumber}`;
      const cardShopImageUrl = shopImageUrl || '/assets/icons/DefaultShop.png';

      // Create a complete HTML document for A4 printing
      const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${shopName} - Business Cards (A4)</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        @page {
            size: A4;
            margin: 5mm;
        }

        body {
            font-family: Arial, sans-serif;
            background: white;
        }

        .page {
            width: 210mm;
            height: 297mm;
            padding: 5mm;
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            grid-template-rows: repeat(4, 1fr);
            gap: 2.5mm;
            page-break-after: avoid;
        }

        .business-card {
            width: 90mm;
            height: 55mm;
            border: 1px solid #E0E0E0;
            border-radius: 3mm;
            padding: 3mm;
            background: ${cardBackgroundColor};
            display: flex;
            flex-direction: column;
            position: relative;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .card-header {
            display: flex;
            align-items: center;
            gap: 2mm;
            margin-bottom: 2mm;
            flex: 1;
        }

        .shop-logo {
            width: 8mm;
            height: 8mm;
            border-radius: 50%;
            border: 1px solid #CCCCCC;
            object-fit: cover;
            background: #F5F5F5;
        }

        .shop-info {
            flex: 1;
        }

        .shop-name {
            font-size: 3.2mm;
            font-weight: bold;
            color: ${primaryTextColor};
            line-height: 1.2;
            margin-bottom: 0.5mm;
        }

        .shop-mobile {
            font-size: 2.3mm;
            color: ${secondaryTextColor};
        }

        .separator {
            display: none;
        }

        .message {
            text-align: center;
            margin-top: 2.5mm;
            margin-bottom: 1.5mm;
            flex: 0.7;
        }

        .message-text {
            font-size: 2mm;
            color: ${tertiaryTextColor};
            line-height: 1.3;
        }

        .qr-section {
            display: flex;
            justify-content: center;
            align-items: center;
            flex: 3.7;
            padding: 2mm 0;
        }

        .qr-code {
            width: 28mm;
            height: 28mm;
            object-fit: contain;
        }

        .card-footer {
            text-align: center;
            margin-top: 1mm;
        }

        .footer-text {
            font-size: 1.5mm;
            color: ${tertiaryTextColor};
            font-style: italic;
        }

        @media print {
            body {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
            }
            .business-card {
                break-inside: avoid;
            }
        }
    </style>
</head>
<body>
    <div class="page">
        ${Array.from({ length: 12 }, (_, i) => `
        <div class="business-card">
            <div class="card-header">
                <img src="${cardShopImageUrl}" alt="Shop Logo" class="shop-logo" />
                <div class="shop-info">
                    <div class="shop-name">${shopName}</div>
                    <div class="shop-mobile">📞 ${mobileNumber}</div>
                </div>
            </div>

            <div class="message">
                <div class="message-text">Scan QR Code using Camera or Google to View Our Products</div>
            </div>

            <div class="qr-section">
                <img src="${qrCode.qrCode}" alt="QR Code" class="qr-code" />
            </div>

            <div class="card-footer">
                <div class="footer-text">Powered by bazly.in</div>
            </div>
        </div>
        `).join('')}
    </div>
</body>
</html>`;

      // Create a new window/tab with the HTML content
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (!printWindow) {
        Alert.alert('Error', 'Please allow popups and try again.');
        return;
      }

      // Write the HTML content
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait a moment for images to load, then trigger print
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();

        // Optional: Close the window after printing
        setTimeout(() => {
          try {
            printWindow.close();
          } catch (e) {
            // User might have closed it manually
          }
        }, 1000);
      }, 1000);

      console.log('✅ [Business Card] Print dialog opened successfully');

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate business cards';
      console.error('❌ Business Card Error:', err);
      if (typeof window !== 'undefined') {
        Alert.alert('Error', `Failed to generate business cards: ${errorMessage}`);
      }
    }
  };

  /**
    try {
      console.log('📥 [Business Card] Starting business card generation...');

      if (!qrCode?.qrCode) {
        console.error('❌ QR code not available');
        Alert.alert('Error', 'QR code not available. Please try again.');
        return;
      }

      if (typeof window === 'undefined') {
        return;
      }

      // Create HTML content for printable business cards
      const shopName = shopDetails?.name || 'My Shop';
      const mobileNumber = `${shopDetails?.mobileCountryCode}${shopDetails?.mobileNumber}`;
      const cardShopImageUrl = shopImageUrl || '/assets/icons/DefaultShop.png';

      // Create a complete HTML document for printing
      const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${shopName} - Business Cards</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        @page {
            size: A4;
            margin: 5mm;
        }

        body {
            font-family: Arial, sans-serif;
            background: white;
        }

        .page {
            width: 210mm;
            height: 297mm;
            padding: 5mm;
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            grid-template-rows: repeat(4, 1fr);
            gap: 2.5mm;
            page-break-after: avoid;
        }

        .business-card {
            width: 90mm;
            height: 55mm;
            border: 1px solid #E0E0E0;
            border-radius: 3mm;
            padding: 3mm;
            background: ${cardBackgroundColor};
            display: flex;
            flex-direction: column;
            position: relative;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .card-header {
            display: flex;
            align-items: center;
            gap: 2mm;
            margin-bottom: 2mm;
            flex: 1;
        }

        .shop-logo {
            width: 8mm;
            height: 8mm;
            border-radius: 50%;
            border: 1px solid #CCCCCC;
            object-fit: cover;
            background: #F5F5F5;
        }

        .shop-info {
            flex: 1;
        }

        .shop-name {
            font-size: 3.2mm;
            font-weight: bold;
            color: ${primaryTextColor};
            line-height: 1.2;
            margin-bottom: 0.5mm;
        }

        .shop-mobile {
            font-size: 2.3mm;
            color: ${secondaryTextColor};
        }

        .separator {
            display: none;
        }

        .message {
            text-align: center;
            margin-top: 2.5mm;
            margin-bottom: 1.5mm;
            flex: 0.7;
        }

        .message-text {
            font-size: 2mm;
            color: ${tertiaryTextColor};
            line-height: 1.3;
        }

        .qr-section {
            display: flex;
            justify-content: center;
            align-items: center;
            flex: 3.7;
            padding: 2mm 0;
        }

        .qr-code {
            width: 28mm;
            height: 28mm;
            object-fit: contain;
        }

        .card-footer {
            text-align: center;
            margin-top: 1mm;
        }

        .footer-text {
            font-size: 1.5mm;
            color: ${tertiaryTextColor};
            font-style: italic;
        }

        @media print {
            body {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
            }
            .business-card {
                break-inside: avoid;
            }
        }
    </style>
</head>
<body>
    <div class="page">
        ${Array.from({ length: 12 }, (_, i) => `
        <div class="business-card">
            <div class="card-header">
                <img src="${cardShopImageUrl}" alt="Shop Logo" class="shop-logo" />
                <div class="shop-info">
                    <div class="shop-name">${shopName}</div>
                    <div class="shop-mobile">📞 ${mobileNumber}</div>
                </div>
            </div>

            <div class="message">
                <div class="message-text">Scan QR Code using Camera or Google to View Our Products</div>
            </div>

            <div class="qr-section">
                <img src="${qrCode.qrCode}" alt="QR Code" class="qr-code" />
            </div>

            <div class="card-footer">
                <div class="footer-text">Powered by bazly.in</div>
            </div>
        </div>
        `).join('')}
    </div>
</body>
</html>`;

      // Create a new window/tab with the HTML content
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (!printWindow) {
        Alert.alert('Error', 'Please allow popups and try again.');
        return;
      }

      // Write the HTML content
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait a moment for images to load, then trigger print
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();

        // Optional: Close the window after printing
        setTimeout(() => {
          try {
            printWindow.close();
          } catch (e) {
            // User might have closed it manually
          }
        }, 1000);
      }, 1000);

      console.log('✅ [Business Card] Print dialog opened successfully');

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate business cards';
      console.error('❌ Business Card Error:', err);
      if (typeof window !== 'undefined') {
        Alert.alert('Error', `Failed to generate business cards: ${errorMessage}`);
      }
    }
  };

  /**
   * Print business cards to multiple cards per A4 page
   */
  const downloadBusinessCardPDF = async () => {
    // This function prints business cards - same as downloadBusinessCardPDFFile
    // Keeping for backward compatibility
    await downloadBusinessCardPDFFile();
  };

  /**
   * Download QR code as image
   */
  const downloadQRCode = async () => {
    try {
      console.log('📥 [QR Download] Starting download...');

      if (!qrCode?.qrCode) {
        console.error('❌ QR code not available');
        return;
      }

      // If we have the enhanced preview, use it directly
      if (enhancedQRPreview) {
        console.log('💾 [QR Download] Using existing preview for download');
        const shopName = shopDetails?.name || 'My Shop';
        const link = document.createElement('a') as HTMLAnchorElement;
        link.href = enhancedQRPreview;
        link.download = `${shopName.replace(/[^a-z0-9]/gi, '_')}_QR_Code.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        console.log('✅ [QR Download] Downloaded successfully');
        return;
      }

      // Otherwise generate it (fallback)
      console.log('🎨 [QR Download] Generating QR code for download...');
      await generateEnhancedQRPreview();

      // After generation, download it
      if (enhancedQRPreview) {
        const shopName = shopDetails?.name || 'My Shop';
        const link = document.createElement('a') as HTMLAnchorElement;
        link.href = enhancedQRPreview;
        link.download = `${shopName.replace(/[^a-z0-9]/gi, '_')}_QR_Code.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        console.log('✅ [QR Download] Downloaded successfully');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to download QR code';
      console.error('❌ Download QR Code Error:', err);
    }
  };

  /**
   * Share shop URL - Simple share of the shop menu URL
   */
  const shareShopUrl = async () => {
    try {
      console.log('📤 [Share] Sharing shop URL...');

      if (!tokens?.shopId) {
        console.error('❌ Shop ID not available');
        return;
      }

      const shopName = shopDetails?.name || 'My Shop';
      const customerViewUrl = getCustomerShopMenuUrl(tokens.shopId);
      const shareMessage = `🏪 ${shopName} - Digital Menu\n\n📱 Scan the QR code or visit our menu online\n\n🔗 ${customerViewUrl}`;

      // Use Web Share API if available
      if (typeof window !== 'undefined' && navigator.share) {
        try {
          await navigator.share({
            title: `${shopName} - Menu`,
            text: shareMessage,
          });
          console.log('✅ [Share] Shop URL shared successfully');
        } catch (shareError) {
          if (shareError instanceof Error && shareError.name === 'AbortError') {
            console.log('ℹ️ [Share] Share dialog closed by user');
            return;
          }
          // Fallback to clipboard
          await copyToClipboard();
        }
      } else {
        // No share API, use clipboard
        await copyToClipboard();
      }

      // Clipboard fallback
      async function copyToClipboard() {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(shareMessage);
          console.log('✅ [Share] URL copied to clipboard');
          if (typeof window !== 'undefined' && window.alert) {
            window.alert('✅ Shop URL copied to clipboard!\n\nYou can now paste and share it.');
          }
        } else {
          console.log('ℹ️ [Share] Clipboard not available');
          if (typeof window !== 'undefined' && window.alert) {
            window.alert(`Shop URL:\n${customerViewUrl}\n\nPlease copy manually.`);
          }
        }
      }
    } catch (err) {
      console.error('❌ [Share] Error:', err);
      if (typeof window !== 'undefined' && window.alert) {
        window.alert('❌ Failed to share. Please try again.');
      }
    }
  };

  /**
   * ========================================================================
   * DEVZONE B2B API MANAGEMENT FUNCTIONS
   * ========================================================================
   */

  /**
   * Load all API keys for the shop
   */
  const loadApiKeys = async () => {
    try {
      setLoadingApiKeys(true);
      console.log('🔑 [DevZone] Loading API keys...');
      const response = await devZoneService.listApiKeys();
      setApiKeys(response.data || []);
      console.log(`✅ [DevZone] Loaded ${response.data?.length || 0} API keys`);
    } catch (error: any) {
      console.error('❌ [DevZone] Failed to load API keys:', error);
      if (typeof window !== 'undefined') {
        Alert.alert('Error', error.message || 'Failed to load API keys');
      }
    } finally {
      setLoadingApiKeys(false);
    }
  };

  /**
   * Load available API scopes
   */
  const loadAvailableScopes = async () => {
    try {
      console.log('📑 [DevZone] Loading available scopes...');
      const response = await devZoneService.getAvailableScopes();
      setAvailableScopes(response.data || {});
      console.log('✅ [DevZone] Available scopes loaded');
    } catch (error: any) {
      console.error('❌ [DevZone] Failed to load scopes:', error);
    }
  };

  /**
   * Create a new API key
   */
  const createApiKey = async () => {
    try {
      // Validation
      if (!newKeyData.name.trim()) {
        Alert.alert('Validation Error', 'Please enter a name for the API key');
        return;
      }
      if (newKeyData.scopes.length === 0) {
        Alert.alert('Validation Error', 'Please select at least one API scope');
        return;
      }

      setCreatingApiKey(true);
      console.log('🔑 [DevZone] Creating new API key...');
      const response = await devZoneService.createApiKey(newKeyData);

      // Store the secret key (shown only once!)
      setCreatedKeySecret(response.data.secretKey || null);

      // Reload API keys list
      await loadApiKeys();

      console.log('✅ [DevZone] API key created successfully');
      Alert.alert(
        'Success',
        'API key created successfully! Please save the secret key now - it will not be shown again.',
      );
    } catch (error: any) {
      console.error('❌ [DevZone] Failed to create API key:', error);
      Alert.alert('Error', error.message || 'Failed to create API key');
    } finally {
      setCreatingApiKey(false);
    }
  };

  /**
   * Revoke an API key
   */
  const revokeApiKey = async (keyId: string, keyName: string) => {
    if (typeof window !== 'undefined') {
      const confirmed = window.confirm(
        `Are you sure you want to revoke "${keyName}"? This action cannot be undone.`
      );

      if (confirmed) {
        try {
          console.log('🗑️ [DevZone] Revoking API key:', keyId);
          await devZoneService.revokeApiKey(keyId, 'Revoked by admin');
          await loadApiKeys();
          setShowKeyDetailsModal(false);
          setSelectedApiKey(null);
          Alert.alert('Success', 'API key revoked successfully');
        } catch (error: any) {
          console.error('❌ [DevZone] Failed to revoke API key:', error);
          Alert.alert('Error', error.message || 'Failed to revoke API key');
        }
      }
    }
  };

  /**
   * Rotate API key secret
   */
  const rotateApiKeySecret = async (keyId: string, keyName: string) => {
    if (typeof window !== 'undefined') {
      const confirmed = window.confirm(
        `Generate a new secret for "${keyName}"? The old secret will stop working immediately.`
      );

      if (confirmed) {
        try {
          console.log('🔄 [DevZone] Rotating API key secret:', keyId);
          const response = await devZoneService.rotateApiKey(keyId);
          const newSecret = response.data.secretKey || null;

          // Store the rotated secret for display
          setRotatedKeySecret(newSecret);

          // Update the selected API key with new data from response
          setSelectedApiKey(response.data);

          await loadApiKeys();
          console.log('✅ [DevZone] Key rotated successfully, new secret available');
        } catch (error: any) {
          console.error('❌ [DevZone] Failed to rotate API key:', error);
          Alert.alert('Error', error.message || 'Failed to rotate API key');
        }
      }
    }
  };

  /**
   * Copy text to clipboard with visual feedback
   */
  const copyToClipboard = async (text: string, label: string) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        setCopiedField(label);
        setTimeout(() => setCopiedField(null), 2000);
        console.log(`✅ [DevZone] Copied ${label} to clipboard`);
      }
    } catch (error) {
      console.error('❌ [DevZone] Failed to copy to clipboard:', error);
    }
  };

  /**
   * Toggle scope selection
   */
  const toggleScope = (scope: string) => {
    setNewKeyData(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter(s => s !== scope)
        : [...prev.scopes, scope],
    }));
  };

  /**
   * Reset create key form
   */
  const resetCreateKeyForm = () => {
    setNewKeyData({
      name: '',
      description: '',
      scopes: [],
      allowedOrigins: [],
      expiresInDays: 365,
    });
    setCreatedKeySecret(null);
    setShowCreateKeyModal(false);
  };


  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: themeColors.secondary }]}>
        <ShopHeader
          shopImage={shopImageUrl || undefined}
          shopName={shopDetails?.name}
          onManageShop={onManageShop}
          onManageUser={onManageUser}
          onManageCatalog={onManageCatalog}
          onManageOrders={onManageOrders}
          onWebsiteBuilder={onWebsiteBuilder}
          onLogout={onLogout}
        />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text variant="body" color="secondary" style={styles.loadingText}>
            Loading shop details...
          </Text>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: themeColors.secondary }]}>
        <ShopHeader
          shopImage={shopImageUrl || undefined}
          shopName={shopDetails?.name}
          onManageShop={onManageShop}
          onManageUser={onManageUser}
          onManageCatalog={onManageCatalog}
          onManageOrders={onManageOrders}
          onWebsiteBuilder={onWebsiteBuilder}
          onLogout={onLogout}
        />
        <View style={styles.errorContainer}>
          <Text variant="h3" color="error" style={styles.errorTitle}>
            Failed to Load
          </Text>
          <Text variant="body" color="secondary" style={styles.errorText}>
            {error}
          </Text>
          <Button onPress={loadShopDetails} style={styles.retryButton}>
            Retry
          </Button>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: themeColors.secondary }]}>
      <ShopHeader
        shopImage={shopImageUrl || undefined}
        shopName={shopDetails?.name}
        onManageShop={onManageShop}
        onManageUser={onManageUser}
        onManageCatalog={onManageCatalog}
        onManageOrders={onManageOrders}
        onWebsiteBuilder={onWebsiteBuilder}
        onLogout={onLogout}
      />

      <ScrollView contentContainerStyle={styles.content}>
        {/* Welcome Card - REMOVED - Now in Header */}

        {/* Shop Details Card */}
        <View style={styles.section}>
          <View style={styles.sectionHeaderRow}>
            <Text variant="h3" bold style={styles.sectionTitle}>
              Shop Details
            </Text>
            {qrCode?.qrCodeUrl && (
              <TouchableOpacity
                onPress={shareShopUrl}
                style={styles.shareIconButton}
                activeOpacity={0.7}
              >
                <img
                  src="/assets/icons/share-icon.png"
                  alt="Share"
                  style={{
                    width: 28,
                    height: 28,
                    objectFit: 'contain',
                  }}
                />
              </TouchableOpacity>
            )}
          </View>

          <Card style={styles.detailsCard}>

            {/* Shop URL - Non-editable, clickable field */}
            {shopDetails?.shopUrl && (
              <View style={styles.detailRow}>
                <Text variant="body" color="secondary" style={styles.detailLabel}>
                  Shop URL:
                </Text>
                <TouchableOpacity
                  onPress={() => {
                    if (typeof window !== 'undefined' && shopDetails?.shopUrl) {
                      window.open(`https://${shopDetails.shopUrl}`, '_blank');
                    }
                  }}
                  activeOpacity={0.7}
                  style={styles.shopUrlButton}
                >
                  <Text variant="body" bold style={styles.shopUrlText}>
                    {shopDetails.shopUrl}
                  </Text>
                  <Text style={styles.externalLinkIcon}>🔗</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Shop Status Indicators */}
            <View style={styles.detailRow}>
              <Text variant="body" color="secondary" style={styles.detailLabel}>
                Shop Status:
              </Text>
              <View style={styles.statusIndicatorContainer}>
                <View
                  style={[
                    styles.statusBall,
                    { backgroundColor: shopDetails?.isLive ? '#4CAF50' : '#F44336' },
                  ]}
                />
                <Text variant="body" bold style={styles.detailValue}>
                  {shopDetails?.isLive ? 'Online' : 'Offline'}
                </Text>
              </View>
            </View>

            <View style={styles.detailRow}>
              <Text variant="body" color="secondary" style={styles.detailLabel}>
                Online Orders:
              </Text>
              <View style={styles.statusIndicatorContainer}>
                <View
                  style={[
                    styles.statusBall,
                    { backgroundColor: shopDetails?.acceptOnlineOrders ? '#4CAF50' : '#F44336' },
                  ]}
                >
                  {shopDetails?.acceptOnlineOrders && (
                    <Text style={styles.tickMark}>✓</Text>
                  )}
                </View>
                <Text variant="body" bold style={styles.detailValue}>
                  {shopDetails?.acceptOnlineOrders ? 'Enabled' : 'Disabled'}
                </Text>
              </View>
            </View>

            {/* Divider before other details */}
            <View style={styles.detailDivider} />

            <View style={styles.detailRow}>
              <Text variant="body" color="secondary" style={styles.detailLabel}>
                Shop Type:
              </Text>
              <Text variant="body" bold style={styles.detailValue}>
                {shopDetails?.shopType || 'N/A'}
              </Text>
            </View>

            <View style={styles.detailRow}>
              <Text variant="body" color="secondary" style={styles.detailLabel}>
                Address:
              </Text>
              <Text variant="body" bold style={styles.detailValue}>
                {shopDetails?.address || 'N/A'}
              </Text>
            </View>

            <View style={styles.detailRow}>
              <Text variant="body" color="secondary" style={styles.detailLabel}>
                Email:
              </Text>
              <Text variant="body" bold style={styles.detailValue}>
                {shopDetails?.email || 'N/A'}
              </Text>
            </View>

            <View style={styles.detailRow}>
              <Text variant="body" color="secondary" style={styles.detailLabel}>
                Mobile:
              </Text>
              <Text variant="body" bold style={styles.detailValue}>
                {shopDetails?.mobileCountryCode}
                {shopDetails?.mobileNumber || 'N/A'}
              </Text>
            </View>

            {shopDetails?.createdAt && (
              <View style={styles.detailRow}>
                <Text variant="body" color="secondary" style={styles.detailLabel}>
                  Created:
                </Text>
                <Text variant="body" bold style={styles.detailValue}>
                  {new Date(shopDetails.createdAt).toLocaleDateString()}
                </Text>
              </View>
            )}
          </Card>
        </View>

        {/* My Shop Business Card Section */}
        <View style={styles.section}>
          <View style={styles.sectionHeaderRow}>
            <Text variant="h3" bold style={styles.sectionTitle}>
              My Shop Business Card
            </Text>

            {/* Action Icons */}
            <View style={styles.cardActionIcons}>
              {/* Edit Background Color Icon */}
              <TouchableOpacity
                onPress={() => setShowCardBackgroundPicker(true)}
                style={styles.cardActionIcon}
                activeOpacity={0.7}
              >
                <Text style={styles.cardActionIconText}>🎨</Text>
              </TouchableOpacity>

              {/* Download A4 PDF Icon */}
              <TouchableOpacity
                onPress={downloadBusinessCardPDFFile}
                style={[styles.cardActionIcon, { backgroundColor: '#2196F3' }]}
                activeOpacity={0.7}
              >
                <Text style={styles.cardActionIconText}>⬇️</Text>
              </TouchableOpacity>

              {/* Print Icon */}
              <TouchableOpacity
                onPress={downloadBusinessCardPDF}
                style={[styles.cardActionIcon, { backgroundColor: accentColor }]}
                activeOpacity={0.7}
              >
                <Text style={styles.cardActionIconText}>🖨️</Text>
              </TouchableOpacity>
            </View>
          </View>

          <Card style={styles.businessCardContainer}>
            {/* Business Card Preview */}
            <View style={styles.businessCardPreview}>
              {/* Business Card Design - 90mm x 55mm ratio */}
              <View style={[styles.businessCard, { backgroundColor: cardBackgroundColor }]}>
                {/* Top Section - Shop Logo, Name & Mobile */}
                <View style={styles.businessCardHeader}>
                  {/* Shop Logo (if available) */}
                  {(shopImageUrl || true) && (
                    <Image
                      source={{ uri: shopImageUrl || '/assets/icons/DefaultShop.png' }}
                      style={styles.businessCardLogo}
                      resizeMode="cover"
                    />
                  )}

                  <View style={styles.businessCardHeaderText}>
                    <Text style={[styles.businessCardShopName, { color: primaryTextColor }]} numberOfLines={2}>
                      {shopDetails?.name || 'My Shop'}
                    </Text>
                    <Text style={[styles.businessCardMobile, { color: secondaryTextColor }]}>
                      📞 {shopDetails?.mobileCountryCode}{shopDetails?.mobileNumber}
                    </Text>
                  </View>
                </View>

                {/* Middle Section - Compact Message */}
                <View style={styles.businessCardMessage}>
                  <Text style={[styles.businessCardMessageText, { color: tertiaryTextColor }]}>
                    Scan QR Code using Camera or Google to View Our Products
                  </Text>
                </View>

                {/* Bottom Section - Larger QR Code */}
                <View style={styles.businessCardQR}>
                  {qrCode?.qrCode && (
                    <Image
                      source={{ uri: qrCode.qrCode }}
                      style={styles.businessCardQRImage}
                      resizeMode="contain"
                    />
                  )}
                </View>

                {/* Footer - Powered by bazly.in */}
                <View style={styles.businessCardFooter}>
                  <Text style={[styles.businessCardFooterText, { color: tertiaryTextColor }]}>
                    Powered by bazly.in
                  </Text>
                </View>
              </View>
            </View>
          </Card>
        </View>

        {/* QR Code Section */}
        <View style={styles.section}>
          <View style={styles.sectionHeaderRow}>
            <Text variant="h3" bold style={styles.sectionTitle}>
              My Shop QR Code
            </Text>

            {/* Print QR Code Icon */}
            {qrCode?.qrCode && (
              <TouchableOpacity
                onPress={downloadQRCode}
                style={[styles.cardActionIcon, { backgroundColor: accentColor }]}
                activeOpacity={0.7}
              >
                <Text style={styles.cardActionIconText}>🖨️</Text>
              </TouchableOpacity>
            )}
          </View>

          <Card style={styles.qrCard}>
            {/* QR Code Image - Enhanced Preview */}
            {qrCode?.qrCode && (
              <View style={styles.qrContainer}>
                {enhancedQRPreview ? (
                  <>
                    {/* Enhanced QR Preview */}
                    <View style={styles.enhancedPreviewContainer}>
                      <Image
                        source={{ uri: enhancedQRPreview }}
                        style={styles.enhancedQRImage}
                        resizeMode="contain"
                      />
                      <View style={styles.previewFeatures}>
                        <Text variant="caption" style={styles.featureItem}>
                          ✓ Shop branding included
                        </Text>
                        <Text variant="caption" style={styles.featureItem}>
                          ✓ Professional design
                        </Text>
                        <Text variant="caption" style={styles.featureItem}>
                          ✓ Ready to print or share
                        </Text>
                      </View>
                    </View>
                  </>
                ) : generatingPreview ? (
                  <View style={styles.loadingPreview}>
                    <ActivityIndicator size="large" color={accentColor} />
                    <Text variant="body" style={styles.previewLoadingText}>
                      Generating enhanced QR preview...
                    </Text>
                  </View>
                ) : (
                  <>
                    {/* Fallback: Simple QR Code */}
                    <Image
                      source={{ uri: qrCode.qrCode }}
                      style={styles.qrImage}
                      resizeMode="contain"
                    />
                    <View style={styles.qrTextContainer}>
                      <Text variant="caption" style={styles.qrText}>
                        {QR_CODE_TEXT}
                      </Text>
                    </View>
                  </>
                )}
              </View>
            )}
          </Card>
        </View>

        {/* DevZone B2B API Management Section */}
        <View style={styles.section}>
          <View style={styles.sectionHeaderRow}>
            <View>
              <Text variant="h3" bold style={styles.sectionTitle}>
                🔐 Enable Bazly B2B APIs
              </Text>
              <Text variant="caption" color="secondary" style={styles.sectionSubtitle}>
                Create API keys to integrate with third-party apps
              </Text>
            </View>
          </View>

          <Card style={styles.devZoneCard}>
            {/* API Keys List Header */}
            <View style={styles.devZoneHeader}>
              <View style={styles.devZoneHeaderLeft}>
                <Text variant="body" bold>
                  API Keys ({apiKeys.length})
                </Text>
                <Text variant="caption" color="secondary">
                  Manage your B2B integration keys
                </Text>
              </View>
              <TouchableOpacity
                style={[styles.createKeyButton, { backgroundColor: accentColor }]}
                onPress={() => setShowCreateKeyModal(true)}
                activeOpacity={0.7}
              >
                <Text style={styles.createKeyButtonText}>+ Create API Key</Text>
              </TouchableOpacity>
            </View>

            {/* Loading State */}
            {loadingApiKeys && (
              <View style={styles.devZoneLoading}>
                <ActivityIndicator size="small" color={accentColor} />
                <Text variant="caption" color="secondary" style={styles.devZoneLoadingText}>
                  Loading API keys...
                </Text>
              </View>
            )}

            {/* No API Keys State */}
            {!loadingApiKeys && apiKeys.length === 0 && (
              <View style={styles.devZoneEmpty}>
                <Text style={styles.devZoneEmptyIcon}>🔑</Text>
                <Text variant="body" bold style={styles.devZoneEmptyTitle}>
                  No API Keys Yet
                </Text>
                <Text variant="caption" color="secondary" style={styles.devZoneEmptyText}>
                  Create your first API key to start integrating with Bazly APIs
                </Text>
              </View>
            )}

            {/* API Keys List */}
            {!loadingApiKeys && apiKeys.length > 0 && (
              <View style={styles.apiKeysList}>
                {apiKeys.map((key, index) => (
                  <TouchableOpacity
                    key={key.keyId}
                    style={[
                      styles.apiKeyItem,
                      index < apiKeys.length - 1 && styles.apiKeyItemBorder,
                    ]}
                    onPress={() => {
                      setSelectedApiKey(key);
                      setShowKeyDetailsModal(true);
                    }}
                    activeOpacity={0.7}
                  >
                    <View style={styles.apiKeyItemLeft}>
                      <View style={styles.apiKeyItemHeader}>
                        <Text variant="body" bold style={styles.apiKeyName}>
                          {key.name}
                        </Text>
                        <View
                          style={[
                            styles.statusBadge,
                            {
                              backgroundColor:
                                key.status === 'ACTIVE'
                                  ? '#34C75920'
                                  : key.status === 'EXPIRED'
                                  ? '#FF950020'
                                  : '#FF2D5520',
                            },
                          ]}
                        >
                          <Text
                            variant="caption"
                            style={[
                              styles.statusBadgeText,
                              {
                                color:
                                  key.status === 'ACTIVE'
                                    ? '#34C759'
                                    : key.status === 'EXPIRED'
                                    ? '#FF9500'
                                    : '#FF2D55',
                              },
                            ]}
                          >
                            {key.status}
                          </Text>
                        </View>
                      </View>
                      <Text variant="caption" color="secondary" style={styles.apiKeyId}>
                        {key.keyId}
                      </Text>
                      <View style={styles.apiKeyScopes}>
                        {key.scopes.slice(0, 3).map((scope) => (
                          <View key={scope} style={styles.scopeChip}>
                            <Text variant="caption" style={styles.scopeChipText}>
                              {scope}
                            </Text>
                          </View>
                        ))}
                        {key.scopes.length > 3 && (
                          <Text variant="caption" color="secondary">
                            +{key.scopes.length - 3} more
                          </Text>
                        )}
                      </View>
                      <Text variant="caption" color="secondary" style={styles.apiKeyDate}>
                        Created: {new Date(key.createdAt).toLocaleDateString()}
                      </Text>
                    </View>
                    <View style={styles.apiKeyItemRight}>
                      <Text style={styles.apiKeyChevron}>›</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {/* Info Banner */}
            <View style={styles.devZoneInfoBanner}>
              <Text style={styles.devZoneInfoIcon}>💡</Text>
              <View style={styles.devZoneInfoContent}>
                <Text variant="caption" bold style={styles.devZoneInfoTitle}>
                  What are B2B APIs?
                </Text>
                <Text variant="caption" color="secondary" style={styles.devZoneInfoText}>
                  Build custom apps (Android, iOS, Web) that integrate with your Bazly shop. Access
                  catalogs, orders, and more via secure API keys.
                </Text>
              </View>
            </View>
          </Card>
        </View>

      </ScrollView>

      {/* Create API Key Modal */}
      <Modal
        visible={showCreateKeyModal}
        transparent={true}
        animationType="slide"
        onRequestClose={resetCreateKeyForm}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalOverlay}>
            <View style={styles.apiKeyModal}>
              {/* Modal Header */}
              <View style={styles.apiKeyModalHeader}>
                <Text variant="h3" bold>
                  {createdKeySecret ? '✅ API Key Created' : '🔑 Create API Key'}
                </Text>
                <TouchableOpacity
                  style={styles.modalCloseButton}
                  onPress={resetCreateKeyForm}
                >
                  <Text style={styles.modalCloseText}>✕</Text>
                </TouchableOpacity>
              </View>

              <ScrollView style={styles.apiKeyModalContent}>
                {createdKeySecret ? (
                  /* Success View - Show Secret Key */
                  <View style={styles.createdKeyView}>
                    <View style={styles.warningBanner}>
                      <Text style={styles.warningIcon}>⚠️</Text>
                      <Text variant="caption" bold style={styles.warningText}>
                        Save this secret key now! It will not be shown again.
                      </Text>
                    </View>

                    <View style={styles.secretKeyContainer}>
                      <Text variant="caption" bold style={styles.fieldLabel}>
                        Secret Key:
                      </Text>
                      <View style={styles.secretKeyBox}>
                        <Text style={styles.secretKeyText} selectable>
                          {createdKeySecret}
                        </Text>
                      </View>
                      <TouchableOpacity
                        style={[styles.copyButton, { backgroundColor: accentColor }]}
                        onPress={() => copyToClipboard(createdKeySecret, 'Secret Key')}
                        activeOpacity={0.7}
                      >
                        <Text style={styles.copyButtonText}>
                          {copiedField === 'Secret Key' ? '✓ Copied!' : '📋 Copy Secret Key'}
                        </Text>
                      </TouchableOpacity>
                    </View>

                    <View style={styles.successInstructions}>
                      <Text variant="caption" bold style={styles.instructionsTitle}>
                        Next Steps:
                      </Text>
                      <Text variant="caption" color="secondary">
                        1. Copy and save the secret key in a secure location
                      </Text>
                      <Text variant="caption" color="secondary">
                        2. Use this key in your app's API requests
                      </Text>
                      <Text variant="caption" color="secondary">
                        3. Include it in the X-API-Key header
                      </Text>
                    </View>

                    <TouchableOpacity
                      style={[styles.doneButton, { backgroundColor: accentColor }]}
                      onPress={resetCreateKeyForm}
                      activeOpacity={0.7}
                    >
                      <Text style={styles.doneButtonText}>Done</Text>
                    </TouchableOpacity>
                  </View>
                ) : (
                  /* Create Form */
                  <View style={styles.createKeyForm}>
                    {/* Name Field */}
                    <View style={styles.formField}>
                      <Text variant="caption" bold style={styles.fieldLabel}>
                        Name *
                      </Text>
                      <input
                        type="text"
                        placeholder="e.g., My Android App Key"
                        value={newKeyData.name}
                        onChange={(e) =>
                          setNewKeyData({ ...newKeyData, name: e.target.value })
                        }
                        style={{
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #E0E0E0',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          width: '100%',
                        }}
                      />
                    </View>

                    {/* Description Field */}
                    <View style={styles.formField}>
                      <Text variant="caption" bold style={styles.fieldLabel}>
                        Description (Optional)
                      </Text>
                      <textarea
                        placeholder="e.g., API key for Android order management app"
                        value={newKeyData.description}
                        onChange={(e) =>
                          setNewKeyData({ ...newKeyData, description: e.target.value })
                        }
                        rows={3}
                        style={{
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #E0E0E0',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          width: '100%',
                          resize: 'vertical',
                        }}
                      />
                    </View>

                    {/* Scopes Selection */}
                    <View style={styles.formField}>
                      <Text variant="caption" bold style={styles.fieldLabel}>
                        API Scopes * (Select at least one)
                      </Text>
                      <View style={styles.scopesGrid}>
                        {Object.entries(availableScopes).map(([scope, description]) => (
                          <TouchableOpacity
                            key={scope}
                            style={[
                              styles.scopeOption,
                              newKeyData.scopes.includes(scope) && {
                                ...styles.scopeOptionSelected,
                                backgroundColor: `${accentColor}20`,
                                borderColor: accentColor,
                              },
                            ]}
                            onPress={() => toggleScope(scope)}
                            activeOpacity={0.7}
                          >
                            <View style={styles.scopeOptionHeader}>
                              <Text
                                variant="caption"
                                bold
                                style={[
                                  styles.scopeOptionTitle,
                                  newKeyData.scopes.includes(scope) && { color: accentColor },
                                ]}
                              >
                                {scope}
                              </Text>
                              {newKeyData.scopes.includes(scope) && (
                                <Text style={[styles.scopeCheck, { color: accentColor }]}>✓</Text>
                              )}
                            </View>
                            <Text variant="caption" color="secondary" style={styles.scopeOptionDesc}>
                              {description}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>

                    {/* Expiration Field */}
                    <View style={styles.formField}>
                      <Text variant="caption" bold style={styles.fieldLabel}>
                        Expires In (Days)
                      </Text>
                      <input
                        type="number"
                        placeholder="365"
                        value={newKeyData.expiresInDays}
                        onChange={(e) =>
                          setNewKeyData({
                            ...newKeyData,
                            expiresInDays: parseInt(e.target.value) || 0,
                          })
                        }
                        style={{
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #E0E0E0',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          width: '100%',
                        }}
                      />
                      <Text variant="caption" color="secondary" style={styles.fieldHint}>
                        Set to 0 for no expiration
                      </Text>
                    </View>

                    {/* Allowed Origins (Optional) */}
                    <View style={styles.formField}>
                      <Text variant="caption" bold style={styles.fieldLabel}>
                        Allowed Origins (Optional)
                      </Text>
                      <input
                        type="text"
                        placeholder="e.g., https://myapp.example.com"
                        value={newKeyData.allowedOrigins?.join(', ') || ''}
                        onChange={(e) =>
                          setNewKeyData({
                            ...newKeyData,
                            allowedOrigins: e.target.value
                              .split(',')
                              .map((o) => o.trim())
                              .filter((o) => o),
                          })
                        }
                        style={{
                          padding: '12px',
                          borderRadius: '8px',
                          border: '1px solid #E0E0E0',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          width: '100%',
                        }}
                      />
                      <Text variant="caption" color="secondary" style={styles.fieldHint}>
                        Comma-separated list of allowed origins
                      </Text>
                    </View>

                    {/* Action Buttons */}
                    <View style={styles.formActions}>
                      <TouchableOpacity
                        style={styles.cancelButton}
                        onPress={resetCreateKeyForm}
                        activeOpacity={0.7}
                      >
                        <Text style={styles.cancelButtonText}>Cancel</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[
                          styles.createButton,
                          { backgroundColor: creatingApiKey ? '#999999' : accentColor },
                        ]}
                        onPress={createApiKey}
                        activeOpacity={0.7}
                        disabled={creatingApiKey}
                      >
                        {creatingApiKey ? (
                          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <ActivityIndicator size="small" color="#FFFFFF" style={{ marginRight: 8 }} />
                            <Text style={styles.createButtonText}>Creating...</Text>
                          </View>
                        ) : (
                          <Text style={styles.createButtonText}>Create API Key</Text>
                        )}
                      </TouchableOpacity>
                    </View>
                  </View>
                )}
              </ScrollView>
            </View>
          </View>
        </View>
      </Modal>

      {/* API Key Details Modal */}
      <Modal
        visible={showKeyDetailsModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => {
          setShowKeyDetailsModal(false);
          setSelectedApiKey(null);
          setRotatedKeySecret(null);
        }}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalOverlay}>
            <View style={styles.apiKeyModal}>
              {/* Modal Header */}
              <View style={styles.apiKeyModalHeader}>
                <Text variant="h3" bold>
                  {rotatedKeySecret ? '✅ Secret Key Rotated' : 'API Key Details'}
                </Text>
                <TouchableOpacity
                  style={styles.modalCloseButton}
                  onPress={() => {
                    setShowKeyDetailsModal(false);
                    setSelectedApiKey(null);
                    setRotatedKeySecret(null);
                  }}
                >
                  <Text style={styles.modalCloseText}>✕</Text>
                </TouchableOpacity>
              </View>

              {selectedApiKey && (
                <ScrollView style={styles.apiKeyModalContent}>
                  <View style={styles.keyDetailsView}>
                    {/* Status Badge */}
                    <View
                      style={[
                        styles.statusBadgeLarge,
                        {
                          backgroundColor:
                            selectedApiKey.status === 'ACTIVE'
                              ? '#34C75920'
                              : selectedApiKey.status === 'EXPIRED'
                              ? '#FF950020'
                              : '#FF2D5520',
                        },
                      ]}
                    >
                      <Text
                        variant="body"
                        bold
                        style={[
                          styles.statusBadgeLargeText,
                          {
                            color:
                              selectedApiKey.status === 'ACTIVE'
                                ? '#34C759'
                                : selectedApiKey.status === 'EXPIRED'
                                ? '#FF9500'
                                : '#FF2D55',
                          },
                        ]}
                      >
                        {selectedApiKey.status}
                      </Text>
                    </View>

                    {/* Key Details */}
                    <View style={styles.detailItem}>
                      <Text variant="caption" color="secondary">
                        Name
                      </Text>
                      <Text variant="body" bold>
                        {selectedApiKey.name}
                      </Text>
                    </View>

                    <View style={styles.detailItem}>
                      <Text variant="caption" color="secondary">
                        Key ID
                      </Text>
                      <View style={styles.copyableField}>
                        <Text variant="body" selectable>
                          {selectedApiKey.keyId}
                        </Text>
                        <TouchableOpacity
                          onPress={() => copyToClipboard(selectedApiKey.keyId, 'Key ID')}
                          activeOpacity={0.7}
                        >
                          <Text style={styles.copyIcon}>
                            {copiedField === 'Key ID' ? '✓' : '📋'}
                          </Text>
                        </TouchableOpacity>
                      </View>
                    </View>

                    {/* Rotated Secret Key Display */}
                    {rotatedKeySecret && (
                      <View style={styles.rotatedSecretContainer}>
                        <View style={styles.warningBanner}>
                          <Text style={styles.warningIcon}>⚠️</Text>
                          <Text variant="caption" bold style={styles.warningText}>
                            New Secret Generated! Save it now - it will not be shown again.
                          </Text>
                        </View>

                        <View style={styles.secretKeyContainer}>
                          <Text variant="caption" bold style={styles.fieldLabel}>
                            New Secret Key:
                          </Text>
                          <View style={styles.secretKeyBox}>
                            <Text style={styles.secretKeyText} selectable>
                              {rotatedKeySecret}
                            </Text>
                          </View>
                          <TouchableOpacity
                            style={[styles.copyButton, { backgroundColor: accentColor }]}
                            onPress={() => copyToClipboard(rotatedKeySecret, 'Rotated Secret')}
                            activeOpacity={0.7}
                          >
                            <Text style={styles.copyButtonText}>
                              {copiedField === 'Rotated Secret' ? '✓ Copied!' : '📋 Copy New Secret'}
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    )}

                    <View style={styles.detailItem}>
                      <Text variant="caption" color="secondary">
                        Scopes
                      </Text>
                      <View style={styles.scopesList}>
                        {selectedApiKey.scopes.map((scope) => (
                          <View key={scope} style={styles.scopeChip}>
                            <Text variant="caption" style={styles.scopeChipText}>
                              {scope}
                            </Text>
                          </View>
                        ))}
                      </View>
                    </View>

                    <View style={styles.detailItem}>
                      <Text variant="caption" color="secondary">
                        Created
                      </Text>
                      <Text variant="body">
                        {new Date(selectedApiKey.createdAt).toLocaleString()}
                      </Text>
                    </View>

                    {selectedApiKey.expiresAt && (
                      <View style={styles.detailItem}>
                        <Text variant="caption" color="secondary">
                          Expires
                        </Text>
                        <Text variant="body">
                          {new Date(selectedApiKey.expiresAt).toLocaleString()}
                        </Text>
                      </View>
                    )}

                    {/* Usage Stats */}
                    {selectedApiKey.usageStats && (
                      <View style={styles.detailItem}>
                        <Text variant="caption" color="secondary">
                          Total Requests
                        </Text>
                        <Text variant="body" bold>
                          {selectedApiKey.usageStats.totalRequests || 0}
                        </Text>
                      </View>
                    )}

                    {/* Action Buttons */}
                    <View style={styles.keyDetailsActions}>
                      {selectedApiKey.status === 'ACTIVE' && (
                        <TouchableOpacity
                          style={[styles.rotateButton, { borderColor: accentColor }]}
                          onPress={() =>
                            rotateApiKeySecret(selectedApiKey.keyId, selectedApiKey.name)
                          }
                          activeOpacity={0.7}
                        >
                          <Text style={[styles.rotateButtonText, { color: accentColor }]}>
                            🔄 Rotate Secret
                          </Text>
                        </TouchableOpacity>
                      )}
                      <TouchableOpacity
                        style={styles.revokeButton}
                        onPress={() => revokeApiKey(selectedApiKey.keyId, selectedApiKey.name)}
                        activeOpacity={0.7}
                      >
                        <Text style={styles.revokeButtonText}>🗑️ Revoke Key</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                </ScrollView>
              )}
            </View>
          </View>
        </View>
      </Modal>

      {/* Background Color Picker Modal */}
      <Modal
        visible={showCardBackgroundPicker}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowCardBackgroundPicker(false)}
      >
        <View style={styles.modalContainer}>
          <TouchableOpacity
            style={styles.modalOverlay}
            activeOpacity={1}
            onPress={() => setShowCardBackgroundPicker(false)}
          >
            <View style={styles.colorPickerModal}>
              <View style={styles.colorPickerHeader}>
                <Text variant="h3" bold>
                  Select Card Background
                </Text>
                <TouchableOpacity
                  style={styles.modalCloseButton}
                  onPress={() => setShowCardBackgroundPicker(false)}
                >
                  <Text style={styles.modalCloseText}>✕</Text>
                </TouchableOpacity>
              </View>

              <ScrollView contentContainerStyle={styles.colorPickerContent}>
                <View style={styles.colorGrid}>
                  {cardBackgroundColors.map((color) => (
                    <TouchableOpacity
                      key={color.value}
                      style={[
                        styles.colorOption,
                        { backgroundColor: color.value },
                        cardBackgroundColor === color.value && styles.colorOptionSelected,
                      ]}
                      onPress={() => {
                        setCardBackgroundColor(color.value);
                        setShowCardBackgroundPicker(false);
                      }}
                      activeOpacity={0.7}
                    >
                      {cardBackgroundColor === color.value && (
                        <Text style={styles.colorOptionCheck}>✓</Text>
                      )}
                      <Text style={styles.colorOptionName}>{color.name}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>
          </TouchableOpacity>
        </View>
      </Modal>

      {/* Image Modal - Full Size View */}
      <Modal
        visible={showImageModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowImageModal(false)}
      >
        <View style={styles.modalContainer}>
          <TouchableOpacity
            style={styles.modalOverlay}
            activeOpacity={1}
            onPress={() => setShowImageModal(false)}
          >
            <View style={styles.modalContent}>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowImageModal(false)}
              >
                <Text style={styles.modalCloseText}>✕ Close</Text>
              </TouchableOpacity>

              {shopDetails?.shopImage && (
                <Image
                  source={{ uri: shopDetails.shopImage }}
                  style={styles.modalImage}
                  resizeMode="contain"
                />
              )}

              <Text variant="body" style={styles.modalShopName}>
                {shopDetails?.name}
              </Text>
            </View>
          </TouchableOpacity>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.gray1,
  },
  content: {
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  errorTitle: {
    marginBottom: 8,
  },
  errorText: {
    marginBottom: 16,
    textAlign: 'center',
  },
  retryButton: {
    minWidth: 120,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    marginBottom: 0,
  },
  shareIconButton: {
    padding: 6,
    borderRadius: 8,
    backgroundColor: colors.white,
    justifyContent: 'center',
    alignItems: 'center',
    width: 40,
    height: 40,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.15)',
    elevation: 3,
  },
  shareIconImage: {
    width: 28,
    height: 28,
  },
  shopImageContainer: {
    width: '100%',
    height: 200,
    backgroundColor: colors.gray2,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    position: 'relative', // Added for overlay positioning
  },
  shopImage: {
    width: '100%',
    height: '100%',
  },
  imageOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  imageOverlayText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  shopImagePlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.gray2,
  },
  shopImageFallback: {
    textAlign: 'center',
    fontSize: 16,
    fontWeight: '600',
  },
  imageDivider: {
    height: 1,
    backgroundColor: colors.gray2,
  },
  // Modal styles for full-size image view
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalOverlay: {
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 800,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
    elevation: 10, // For Android
  },
  modalCloseButton: {
    alignSelf: 'flex-end',
    backgroundColor: colors.error,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  modalCloseText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  modalImage: {
    width: '100%',
    height: 400,
    maxHeight: 500,
    borderRadius: 8,
  },
  modalShopName: {
    marginTop: 16,
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
    color: colors.gray8,
  },
  statusRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
  },
  statusIndicatorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statusBall: {
    width: 12,
    height: 12,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tickMark: {
    color: '#fff',
    fontSize: 8,
    fontWeight: 'bold',
    lineHeight: 12,
  },
  detailsCard: {
    paddingVertical: 0,
    paddingHorizontal: 0,
    overflow: 'hidden',
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
    minHeight: 42,
  },
  detailLabel: {
    flex: 0,
    marginRight: 12,
    minWidth: 100,
    fontSize: 14,
  },
  detailValue: {
    flex: 1,
    textAlign: 'right',
    fontSize: 14,
  },
  shopUrlButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
    backgroundColor: colors.gray1,
    flex: 1,
    justifyContent: 'flex-end',
  },
  shopUrlText: {
    color: colors.primary,
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  externalLinkIcon: {
    fontSize: 14,
  },
  detailDivider: {
    height: 8,
    backgroundColor: colors.gray1,
  },
  qrCard: {
    paddingVertical: 24,
    alignItems: 'center',
  },
  qrContainer: {
    alignItems: 'center',
    marginBottom: 20,
    width: '100%',
  },
  qrImage: {
    width: 250,
    height: 250,
    marginBottom: 16,
  },
  qrTextContainer: {
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  qrText: {
    textAlign: 'center',
    color: colors.gray7,
    fontWeight: '600',
  },
  // Enhanced QR Preview Styles
  enhancedPreviewContainer: {
    alignItems: 'center',
    width: '100%',
    maxWidth: 500,
  },
  enhancedQRImage: {
    width: '100%',
    maxWidth: 400,
    aspectRatio: 1000 / 1400, // Match the new rectangular canvas ratio (PhonePe/Paytm style)
    marginBottom: 20,
    borderRadius: 8,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    elevation: 5,
  },
  previewFeatures: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 20,
  },
  featureItem: {
    color: colors.success,
    fontSize: 13,
    fontWeight: '600',
  },
  loadingPreview: {
    alignItems: 'center',
    padding: 40,
  },
  previewLoadingText: {
    marginTop: 16,
    color: colors.gray6,
    textAlign: 'center',
  },
  buttonContainer: {
    width: '100%',
    marginBottom: 16,
    gap: 12,
  },
  downloadButton: {
    marginBottom: 8,
  },
  shareButton: {
    marginBottom: 0,
  },
  urlContainer: {
    backgroundColor: colors.gray1,
    padding: 12,
    borderRadius: 8,
    width: '100%',
  },
  urlLabel: {
    marginBottom: 4,
  },
  urlValue: {
    color: colors.primary,
    fontFamily: 'monospace',
  },
  // Business Card Styles
  businessCardContainer: {
    paddingVertical: 20,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  businessCardPreview: {
    width: '100%',
    maxWidth: 500,
    marginBottom: 20,
  },
  businessCard: {
    width: '100%',
    aspectRatio: 90 / 55, // 90mm x 55mm ratio
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    padding: 16,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    elevation: 4,
  },
  businessCardHeader: {
    flex: 1.5,
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: 4,
    gap: 10,
  },
  businessCardLogo: {
    width: 45,
    height: 45,
    borderRadius: 22.5,
    borderWidth: 1,
    borderColor: '#CCCCCC',
    backgroundColor: '#F5F5F5',
  },
  businessCardHeaderText: {
    flex: 1,
    justifyContent: 'center',
  },
  businessCardShopName: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginBottom: 3,
    lineHeight: 18,
  },
  businessCardMobile: {
    fontSize: 11,
    color: '#333333',
  },
  businessCardSeparator: {
    height: 1,
    backgroundColor: '#E0E0E0',
    marginVertical: 6,
  },
  businessCardMessage: {
    flex: 0.8,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 4,
    marginTop: 10,
  },
  businessCardMessageText: {
    fontSize: 10,
    color: '#555555',
    textAlign: 'center',
    lineHeight: 13,
  },
  businessCardQR: {
    flex: 4.7,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 4,
  },
  businessCardQRImage: {
    width: '90%',
    height: '100%',
    maxWidth: 200,
  },
  businessCardFooter: {
    marginTop: 4,
    alignItems: 'center',
  },
  businessCardFooterText: {
    fontSize: 8,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  // Action Icons Styles
  cardActionIcons: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  cardActionIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E0E0E0',
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  cardActionIconText: {
    fontSize: 20,
  },
  // Color Picker Modal Styles
  colorPickerModal: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    maxWidth: 500,
    width: '90%',
    maxHeight: '70%',
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2)',
    elevation: 8,
  },
  colorPickerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
  },
  colorPickerContent: {
    paddingBottom: 20,
  },
  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'center',
  },
  colorOption: {
    width: 100,
    height: 80,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#E0E0E0',
    justifyContent: 'center',
    alignItems: 'center',
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  colorOptionSelected: {
    borderColor: colors.primary,
    borderWidth: 3,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
    elevation: 4,
  },
  colorOptionCheck: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.success,
    marginBottom: 4,
  },
  colorOptionName: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.gray7,
    textAlign: 'center',
  },

  // ===================================================================
  // DevZone B2B API Management Styles
  // ===================================================================
  sectionSubtitle: {
    marginTop: 4,
    fontSize: 13,
  },
  devZoneCard: {
    padding: 0,
    overflow: 'hidden',
  },
  devZoneHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
  },
  devZoneHeaderLeft: {
    flex: 1,
  },
  createKeyButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  createKeyButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 14,
  },
  devZoneLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    gap: 12,
  },
  devZoneLoadingText: {
    marginLeft: 8,
  },
  devZoneEmpty: {
    alignItems: 'center',
    padding: 40,
  },
  devZoneEmptyIcon: {
    fontSize: 48,
    marginBottom: 12,
  },
  devZoneEmptyTitle: {
    marginBottom: 8,
  },
  devZoneEmptyText: {
    textAlign: 'center',
    maxWidth: 300,
  },
  apiKeysList: {
    paddingVertical: 8,
  },
  apiKeyItem: {
    flexDirection: 'row',
    padding: 16,
    alignItems: 'center',
  },
  apiKeyItemBorder: {
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
  },
  apiKeyItemLeft: {
    flex: 1,
  },
  apiKeyItemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  apiKeyName: {
    fontSize: 15,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  statusBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  apiKeyId: {
    marginBottom: 8,
    fontFamily: 'monospace',
  },
  apiKeyScopes: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 8,
  },
  scopeChip: {
    backgroundColor: colors.gray1,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  scopeChipText: {
    fontSize: 11,
    color: colors.gray7,
  },
  apiKeyDate: {
    fontSize: 12,
  },
  apiKeyItemRight: {
    marginLeft: 12,
  },
  apiKeyChevron: {
    fontSize: 24,
    color: colors.gray5,
  },
  devZoneInfoBanner: {
    flexDirection: 'row',
    padding: 16,
    gap: 12,
    backgroundColor: '#F0F9FF',
    borderTopWidth: 1,
    borderTopColor: colors.gray2,
  },
  devZoneInfoIcon: {
    fontSize: 20,
  },
  devZoneInfoContent: {
    flex: 1,
  },
  devZoneInfoTitle: {
    marginBottom: 4,
    color: '#0066CC',
  },
  devZoneInfoText: {
    lineHeight: 18,
  },

  // API Key Modal Styles
  apiKeyModal: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 0,
    maxWidth: 600,
    width: '90%',
    maxHeight: '85%',
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.25)',
    elevation: 10,
  },
  apiKeyModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
  },
  apiKeyModalContent: {
    flex: 1,
  },
  createdKeyView: {
    padding: 20,
  },
  rotatedSecretContainer: {
    marginVertical: 16,
    paddingVertical: 16,
    paddingHorizontal: 0,
    borderRadius: 12,
    backgroundColor: '#FFFEF0',
    borderWidth: 1,
    borderColor: '#FFE69C',
  },
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    backgroundColor: '#FFF3CD',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FFE69C',
    marginBottom: 20,
  },
  warningIcon: {
    fontSize: 24,
  },
  warningText: {
    flex: 1,
    color: '#856404',
    lineHeight: 18,
  },
  secretKeyContainer: {
    marginBottom: 24,
  },
  fieldLabel: {
    marginBottom: 8,
    color: colors.gray7,
  },
  secretKeyBox: {
    padding: 16,
    backgroundColor: colors.gray1,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.gray3,
    marginBottom: 12,
  },
  secretKeyText: {
    fontFamily: 'monospace',
    fontSize: 13,
    lineHeight: 20,
    wordBreak: 'break-all',
  },
  copyButton: {
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  copyButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 14,
  },
  successInstructions: {
    padding: 16,
    backgroundColor: colors.gray1,
    borderRadius: 8,
    gap: 8,
  },
  instructionsTitle: {
    marginBottom: 4,
    color: colors.gray8,
  },
  doneButton: {
    marginTop: 20,
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  doneButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 15,
  },
  createKeyForm: {
    padding: 20,
    gap: 20,
  },
  formField: {
    gap: 8,
  },
  fieldHint: {
    marginTop: 4,
    fontSize: 12,
  },
  scopesGrid: {
    gap: 12,
  },
  scopeOption: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: colors.gray3,
    backgroundColor: colors.gray1,
  },
  scopeOptionSelected: {
    borderWidth: 2,
  },
  scopeOptionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  scopeOptionTitle: {
    fontWeight: '600',
  },
  scopeCheck: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  scopeOptionDesc: {
    fontSize: 12,
    lineHeight: 16,
  },
  formActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: colors.gray2,
  },
  cancelButtonText: {
    color: colors.gray7,
    fontWeight: '600',
    fontSize: 15,
  },
  createButton: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  createButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 15,
  },

  // Key Details Modal Styles
  keyDetailsView: {
    padding: 20,
    gap: 16,
  },
  statusBadgeLarge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginBottom: 8,
  },
  statusBadgeLargeText: {
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  detailItem: {
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.gray2,
    gap: 6,
  },
  copyableField: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  copyIcon: {
    fontSize: 18,
  },
  scopesList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  keyDetailsActions: {
    gap: 12,
    marginTop: 8,
  },
  rotateButton: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 2,
    alignItems: 'center',
  },
  rotateButtonText: {
    fontWeight: '600',
    fontSize: 14,
  },
  revokeButton: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#FF2D55',
    backgroundColor: '#FFF5F7',
    alignItems: 'center',
  },
  revokeButtonText: {
    color: '#FF2D55',
    fontWeight: '600',
    fontSize: 14,
  },
});

